/**
 * Which answer input the player sees (docs/GAME_DESIGN.md § 6).
 *
 * - Settings give a preference: auto / multiple choice / handwriting.
 * - "auto" means handwriting on touch or pen devices once the player has
 *   calibrated it, otherwise multiple choice.
 * - During a run the player can switch modes with the toggle (session-wide),
 *   and the handwriting pad can fall back to multiple choice for a single
 *   problem. Input mode never changes game logic: every mode submits text
 *   through the same engine call.
 */
import type { InputPreference } from '../contracts.ts';

export type InputMode = 'mc' | 'hw';

/** The order of the Choices / Write options (the in-game radio group). */
export const INPUT_MODES: readonly InputMode[] = ['mc', 'hw'];

/** The mode a one-button switch (the Watch layout) changes to. */
export function otherInputMode(mode: InputMode): InputMode {
  return mode === 'mc' ? 'hw' : 'mc';
}

/**
 * Radio-group keys (WAI-ARIA radio group pattern): the arrow keys move to the
 * next / previous option, wrapping round, and Home / End go to the first /
 * last. The group is one Tab stop, so this is how a keyboard user switches.
 * Null for any other key.
 */
export function modeForRadioKey(key: string, mode: InputMode): InputMode | null {
  const n = INPUT_MODES.length;
  const i = INPUT_MODES.indexOf(mode);
  switch (key) {
    case 'ArrowRight':
    case 'ArrowDown':
      return INPUT_MODES[(i + 1) % n] ?? mode;
    case 'ArrowLeft':
    case 'ArrowUp':
      return INPUT_MODES[(i - 1 + n) % n] ?? mode;
    case 'Home':
      return INPUT_MODES[0] ?? mode;
    case 'End':
      return INPUT_MODES[n - 1] ?? mode;
    default:
      return null;
  }
}

/** What the device tells us about how the player points. */
export interface PointerEnvironment {
  /** `(pointer: coarse)` matches: a finger is the primary pointer. */
  coarsePointer: boolean;
  /** A pen/stylus has been used on this page. */
  penUsed: boolean;
}

/** The mode a run starts in, from the settings preference and the device. */
export function resolveInputMode(preference: InputPreference, env: PointerEnvironment, handwritingCalibrated: boolean): InputMode {
  switch (preference) {
    case 'mc':
      return 'mc';
    case 'hw':
      return 'hw';
    case 'auto':
      return (env.coarsePointer || env.penUsed) && handwritingCalibrated ? 'hw' : 'mc';
  }
}

export interface InputModeState {
  /** Mode picked with the in-game toggle; null = follow the settings preference. */
  override: InputMode | null;
  /** Problem for which the pad fell back to multiple choice (that problem only). */
  fallbackProblemId: string | null;
}

export const INITIAL_INPUT_MODE_STATE: InputModeState = { override: null, fallbackProblemId: null };

/** The mode shown for `problemId`: toggle choice over preference; a pad fallback forces choices for its problem. */
export function effectiveInputMode(base: InputMode, state: InputModeState, problemId: string | null): InputMode {
  const mode = state.override ?? base;
  if (mode === 'hw' && problemId !== null && state.fallbackProblemId === problemId) return 'mc';
  return mode;
}

/** The toggle was used: switch the whole session to `mode` (and forget any per-problem fallback). */
export function chooseInputMode(mode: InputMode): InputModeState {
  return { override: mode, fallbackProblemId: null };
}

/** The pad gave up (or the player tapped "Choices"): multiple choice for this problem only. */
export function fallBackToChoices(state: InputModeState, problemId: string): InputModeState {
  return { ...state, fallbackProblemId: problemId };
}
